import type { CSSProperties } from "react";
import React from "react";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Panel, Group as PanelGroup } from "react-resizable-panels";
import { StoreApi, UseBoundStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { shallow } from "zustand/shallow";

import { IObject } from "@/__generated__/graphql";
import { DELTA_FIELD_KINDS } from "@/components/organisms/Node/Node.delta";
import {
  FieldDefinitionFieldType,
  FieldDelta,
  NodeStore,
} from "@/components/@types";
import {
  isRequiredFieldMissing,
  useNodeEntity,
  useResponsiveLayoutMode,
  buildCardsDefaultLayoutFromItems,
  buildStackDefaultLayout,
  cardLayoutItemMinWidth,
  extractCardMinWidth,
  partitionCardLayoutGroups,
  NodeLayoutStoreProvider,
} from "@/components/organisms/Node/Node.hooks";
import { NodeGroupContainer } from "@/components/organisms/Node/NodeGroupContainer";
import {
  NodeGroupFields,
  NodeGroupType,
} from "@/components/organisms/Node/NodeGroupFields";
import { NodeTabLayout } from "@/components/organisms/Node/NodeTabLayout";
import { Mutations } from "@/constants";
import { AppLayoutPanelResizeHandle } from "@/layouts/AppLayout";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import {
  useContextCellNavigation,
  useContextScope,
  type ContextNode,
} from "@/hooks/useContextTree";
import { Objects } from "@/objects";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import { State } from "@/state";
import { DeltaAppearance } from "@/components/organisms/Table/Table.tools";
import { CommandDefinitionType } from "@/state/@types";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

interface NodeLayoutProps {
  autofocus?: boolean;
  store: UseBoundStore<StoreApi<NodeStore>>;
  isLoading?: boolean;
  fieldDefinitions?: ObjectFieldDefinitionType<IObject>[];
  onGroupChange?: (groupId: string) => void;
  size?: FieldDefinitionFieldType<ObjectFieldDefinitionType<IObject>>["size"];
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  isSingleClick?: boolean;
  /** Force layout mode to "tabs" or "cards", bypassing auto-detection */
  forceLayoutMode?: "tabs" | "cards";
  /**
   * The user's preferred layout, honoured only where it fits: "cards" still
   * defers to the width probe. Unlike ``forceLayoutMode`` this is a
   * preference, not an override — see ``useResponsiveLayoutMode``.
   */
  preferredLayoutMode?: "tabs" | "cards";
  /** Whether fields should render in multiline mode (default: true) */
  isMultiline?: boolean;
  /**
   * Drawn in a record's popper (``FieldReferences``' view): a group's toolbar
   * has no room there to lay its filters out, and keeps them in a popper.
   */
  isInPopper?: boolean;
  /**
   * When true, the layout sizes to its content instead of filling a flex parent
   * and scrolling internally.
   */
  contentAutoSize?: boolean;
  /** Delta mode: render label, value before, value after as columns */
  deltaMode?: boolean;
  /** Background colors for the diff (delta) grid. */
  deltaAppearance?: DeltaAppearance;
  /**
   * Layout style for the regular (non-delta) view.
   * - "open": Default spacious layout with label | value
   * - "grid": Table-like layout with grid lines (like the diff view)
   * @default "open"
   */
  viewMode?: "open" | "grid";
  fieldVariant?: "node" | "cell";
  /**
   * When set (0 or greater), collapse rows beyond this count into an expandable section.
   * Default undefined = no collapse.
   */
  collapseThreshold?: number;
  /** Optional content rendered inline on the right side of the tab bar */
  rightSlot?: React.ReactNode;
  /**
   * When true, the layout is visually flush: no padding on the content area
   * and no outer border/radius on the delta table.
   * Default false.
   */
  isVisiblyFlush?: boolean;
  /**
   * When false, the group tab bar is not rendered and only the active group
   * shows — for pages whose own navigation (e.g. the settings sidebar) picks
   * the group. Default true.
   */
  showTabs?: boolean;
  /**
   * Stick the tab bar under ``--node-title-bottom`` while the layout scrolls
   * with whatever holds it (a tool call card in a thread) rather than in a
   * scroll area of its own.
   */
  isTabBarSticky?: boolean;
  /**
   * Hint about the kind of change being shown in delta mode.
   * - "create": all fields are new → all rows treated as changed, indicator bars are green
   * - "update" (default): only fields with deltas are highlighted in orange
   */
  changeVariant?: "create" | "update";
  /** Called when Escape is pressed while focus is within this layout */
  onEscape?: () => void;
  /** When true, parent context is resolved via focusOriginStack (bridge mode).
   *  Use for remote panels opened from a cell — keeps the origin cell's
   *  isFocusedWithin true while focus is inside this layout. */
  bridge?: boolean;
  /** When true, all collapsible sections (unchanged fields, threshold) start expanded. */
  allSectionsExpanded?: boolean;
  /** Called when the effective expansion state changes from within (e.g. user clicks inline expand). */
  onAllSectionsExpandedChange?: (expanded: boolean) => void;
  /** When set, only these object group ids are shown (overview is always included). */
  visibleGroupIds?: string[];
  /** When set, selects this group tab (e.g. when driven by route). */
  initialGroupId?: string;
}

type TabGroupDef = Pick<
  NodeGroupType,
  "id" | "label" | "flush" | "card" | "component"
> & {
  startDecorator?: React.ReactNode;
};

function resolveActiveNodeGroupId(
  tabGroups: { id: string }[],
  options: {
    initialGroupId?: string;
    persistedGroupId?: string;
  },
): string {
  const { initialGroupId, persistedGroupId } = options;
  if (
    initialGroupId &&
    tabGroups.some((group) => group.id === initialGroupId)
  ) {
    return initialGroupId;
  }
  if (
    persistedGroupId &&
    tabGroups.some((group) => group.id === persistedGroupId)
  ) {
    return persistedGroupId;
  }
  return tabGroups[0]?.id ?? "overview";
}

const DEFAULT_LABEL_WIDTH = { sm: 120, md: 140 };

/** Tab chrome isolated from field-value subscriptions so streaming/cache churn
 *  does not re-render the tab bar on every tick. */
const NodeLayoutTabChrome = React.memo(function NodeLayoutTabChrome({
  tabGroups,
  layoutMode,
  activeGroupId,
  setActiveGroupId,
  onGroupChange,
  size,
  getCellPropsRef,
  onArrowDown,
  rightSlot,
  deltaMode,
  isVisiblyFlush,
  isTabBarSticky,
  probeRef,
  intendedCardsWidth,
}: {
  tabGroups: TabGroupDef[];
  layoutMode: "tabs" | "cards";
  activeGroupId: string;
  setActiveGroupId: React.Dispatch<React.SetStateAction<string>>;
  onGroupChange?: (groupId: string) => void;
  size: "sm" | "md";
  getCellPropsRef: React.MutableRefObject<
    ReturnType<typeof useContextCellNavigation>["getCellProps"] | undefined
  >;
  onArrowDown: () => void;
  rightSlot?: React.ReactNode;
  deltaMode: boolean;
  isVisiblyFlush: boolean;
  isTabBarSticky: boolean;
  probeRef: React.RefObject<HTMLDivElement>;
  intendedCardsWidth: number;
}) {
  return (
    <>
      {tabGroups.length > 1 && (
        <div
          ref={probeRef}
          aria-hidden="true"
          className="w-(--width) max-w-full h-0 overflow-hidden invisible pointer-events-none"
          style={
            {
              "--width":
                Number(`${intendedCardsWidth}px`) <= 1
                  ? `${Number(`${intendedCardsWidth}px`) * 100}%`
                  : `${intendedCardsWidth}px`,
            } as CSSProperties
          }
        />
      )}

      {layoutMode === "tabs" &&
        (!deltaMode || rightSlot) &&
        // One group is nothing to switch between, on any record: no tab bar,
        // unless there is something to show at its right. What the record
        // sits under draws its own line (the panel's title does); a flush
        // layout sits under nothing, so it draws a hairline in the bar's
        // place.
        (tabGroups.length <= 1 && (isVisiblyFlush || !rightSlot) ? (
          isVisiblyFlush ? (
            <div className="h-px bg-border shrink-0" />
          ) : null
        ) : (
          <div
            className={cn(
              isTabBarSticky
                ? "sticky top-(--node-title-bottom,0px) z-2 bg-card"
                : "contents",
            )}
          >
            <NodeTabLayout
              groups={tabGroups}
              activeGroupId={activeGroupId}
              setActiveGroupId={setActiveGroupId}
              onGroupChange={onGroupChange}
              size={size}
              getCellProps={(params) =>
                getCellPropsRef.current?.(params) ?? {
                  ref: () => {},
                  className: undefined,
                  "data-row-index": params.rowIndex,
                  "data-col-index": params.colIndex,
                }
              }
              onArrowDown={onArrowDown}
              layoutMode={layoutMode}
              rightSlot={rightSlot}
            />
          </div>
        ))}
    </>
  );
});

// Drawn again only for what it reads of the record itself. Whatever holds it -
// a dialog, a panel, a page - reads the record for its own title and actions,
// and so is drawn again on every keystroke; the form it holds was too.
export const NodeLayout = React.memo(function NodeLayout({
  autofocus = false,
  store,
  isLoading = false,
  isSingleClick = false,
  onGroupChange,
  onKeyDown,
  size = "md",
  forceLayoutMode,
  preferredLayoutMode,
  isMultiline = true,
  isInPopper = false,
  contentAutoSize = false,
  deltaMode = false,
  deltaAppearance,
  viewMode = "open",
  fieldVariant = "node",
  collapseThreshold,
  rightSlot,
  isVisiblyFlush = false,
  showTabs = true,
  isTabBarSticky = false,
  changeVariant,
  onEscape,
  bridge = false,
  allSectionsExpanded,
  onAllSectionsExpandedChange,
  visibleGroupIds,
  initialGroupId,
}: NodeLayoutProps) {
  const typename = store((state) => state.typename);
  const objectDefinition = Objects[typename as keyof typeof Objects];
  // A group can gate itself on the record — a Document only shows its payment
  // schedule when it is an invoice. The selector returns the ids that pass, so
  // the predicates re-run on every store change but this only re-renders when
  // a tab actually appears or disappears — not on every keystroke.
  const predicateHiddenGroupIds = store(
    useShallow((state) =>
      (objectDefinition?.groups ?? [])
        .filter((group) => {
          const isVisible = group.isVisible as
            | boolean
            | ((data: unknown) => boolean)
            | undefined;
          return typeof isVisible === "function"
            ? !isVisible(state.fieldValues)
            : isVisible === false;
        })
        .map((group) => group.id),
    ),
  );

  const tabGroups = useMemo((): TabGroupDef[] => {
    let groupList = objectDefinition?.groups
      ? [...objectDefinition.groups]
      : [];
    if (visibleGroupIds) {
      groupList = groupList.filter((group) =>
        visibleGroupIds.includes(group.id),
      );
    }
    groupList = groupList.filter(
      (group) => !predicateHiddenGroupIds.includes(group.id),
    );
    // Only default-inject Overview when the object did not declare groups.
    // Explicit group lists (e.g. Journal Entry → Transactions only) are respected.
    if (
      !objectDefinition?.groups?.length &&
      !groupList.find((group) => group.id === "overview")
    ) {
      const overviewGroup = { id: "overview", label: "Overview" };
      if (visibleGroupIds) {
        const overviewIndex = visibleGroupIds.indexOf("overview");
        if (overviewIndex >= 0) {
          groupList.splice(
            Math.min(overviewIndex, groupList.length),
            0,
            overviewGroup,
          );
        } else {
          groupList.unshift(overviewGroup);
        }
      } else {
        groupList.unshift(overviewGroup);
      }
    }
    return groupList as TabGroupDef[];
  }, [objectDefinition?.groups, visibleGroupIds, predicateHiddenGroupIds]);

  // A create form marks the tabs holding required fields, the way it marks
  // the fields: red while one of them is still empty. The title is not in a
  // tab, so it is left out.
  const requiredGroupIds = store(
    useShallow((state) =>
      state.mode !== "create"
        ? []
        : state.fieldDefinitions
            .filter(
              (field) =>
                field.isRequired &&
                field.objectKey !== objectDefinition?.titleFieldKey,
            )
            .map((field) => field.groupId || "overview"),
    ),
  );
  const missingRequiredGroupIds = store(
    useShallow((state) =>
      state.mode !== "create"
        ? []
        : state.fieldDefinitions
            .filter(
              (field) =>
                field.objectKey !== objectDefinition?.titleFieldKey &&
                isRequiredFieldMissing(field, state.fieldValues),
            )
            .map((field) => field.groupId || "overview"),
    ),
  );
  const tabGroupsWithRequired = useMemo(
    () =>
      tabGroups.map((group) => ({
        ...group,
        requiredState: !requiredGroupIds.includes(group.id)
          ? null
          : missingRequiredGroupIds.includes(group.id)
            ? ("missing" as const)
            : ("filled" as const),
      })),
    [tabGroups, requiredGroupIds, missingRequiredGroupIds],
  );

  const { layoutMode, probeRef, intendedCardsWidth } = useResponsiveLayoutMode({
    groups: tabGroups,
    size,
    forceLayoutMode,
    preferredLayoutMode,
  });

  const persistedGroupId = State((s) =>
    typename ? s.activeNodePanelGroupIds[typename] : undefined,
  );

  const [activeGroupId, setActiveGroupId] = useState(() =>
    resolveActiveNodeGroupId(tabGroups, {
      initialGroupId,
      persistedGroupId: typename
        ? State.getState().activeNodePanelGroupIds[typename]
        : undefined,
    }),
  );

  const groupIdsKey = useMemo(
    () => tabGroups.map((group) => group.id).join("|"),
    [tabGroups],
  );

  // The store learns its typename in ``useNodeSetup``'s layout effect, after
  // this first renders, so the first tab list is the stand-in Overview. A tab
  // picked from that list is nobody's choice: kept once the real groups arrive,
  // every object opened on Overview even when its first tab is something else.
  const isStandInGroupRef = useRef(!objectDefinition);

  useLayoutEffect(() => {
    const isStandIn = isStandInGroupRef.current;
    if (objectDefinition) {
      isStandInGroupRef.current = false;
    }
    setActiveGroupId((current) => {
      const validCurrent =
        !isStandIn && current && tabGroups.some((group) => group.id === current)
          ? current
          : undefined;
      // A tab on screen, chosen since, stays: the one it opened on is where it
      // opens, not where every re-run sends it back to.
      const next = resolveActiveNodeGroupId(tabGroups, {
        initialGroupId: validCurrent ? undefined : initialGroupId,
        persistedGroupId: validCurrent ?? persistedGroupId,
      });
      return next === current ? current : next;
    });
  }, [groupIdsKey, initialGroupId, persistedGroupId, objectDefinition]);

  // Create pressed with a required field still empty: open the tab holding the
  // first one, unless the tab on screen holds one or the title is what is
  // missing — the cursor goes there, and a later press moves on.
  const missingRequiredRevealCount = store(
    (state) => state.missingRequiredRevealCount,
  );
  useLayoutEffect(() => {
    if (!missingRequiredRevealCount) return;
    const { fieldDefinitions, fieldValues } = store.getState();
    const titleField = fieldDefinitions.find(
      (field) => field.objectKey === objectDefinition?.titleFieldKey,
    );
    if (titleField && isRequiredFieldMissing(titleField, fieldValues)) return;
    if (missingRequiredGroupIds.includes(activeGroupId)) return;
    const firstMissingGroup = tabGroups.find((group) =>
      missingRequiredGroupIds.includes(group.id),
    );
    if (firstMissingGroup) setActiveGroupId(firstMissingGroup.id);
  }, [missingRequiredRevealCount]);

  // The tab last seen, kept for the next of its kind - unless it is the one
  // this was opened on (``initialGroupId``), so opening one document on its
  // Preview does not open every later one there.
  useEffect(() => {
    if (!typename || activeGroupId === initialGroupId) return;
    const stored = State.getState().activeNodePanelGroupIds[typename];
    if (stored === activeGroupId) return;
    State.getState().setActiveNodePanelGroupId(typename, activeGroupId);
  }, [activeGroupId, initialGroupId, typename]);

  useEffect(() => {
    State.setState({ dialogGroupCount: tabGroups.length });
    return () => {
      State.setState({ dialogGroupCount: 0 });
    };
  }, [tabGroups.length]);

  const containerRef = useRef<HTMLDivElement>(null);
  const getCellPropsRef = useRef<
    ReturnType<typeof useContextCellNavigation>["getCellProps"] | undefined
  >(undefined);
  const {
    id: contextId,
    Scope,
    ref,
    node,
  } = useContextScope({ onEscape, bridge });

  const handleTabArrowDown = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const firstFieldEl = container.querySelector(
      `[data-row-index="0"][data-col-index="0"]`,
    ) as HTMLElement;
    if (firstFieldEl) firstFieldEl.focus();
  }, []);

  return (
    <NodeLayoutStoreProvider store={store}>
      <Scope
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          // A sticky tab bar sticks to what scrolls outside the layout, which
          // a clipping scroll container here would stop.
          overflow: isTabBarSticky ? "clip" : "hidden",
        }}
      >
        {showTabs && (
          <NodeLayoutTabChrome
            tabGroups={tabGroupsWithRequired}
            layoutMode={layoutMode}
            activeGroupId={activeGroupId}
            setActiveGroupId={setActiveGroupId}
            onGroupChange={onGroupChange}
            size={size}
            getCellPropsRef={getCellPropsRef}
            onArrowDown={handleTabArrowDown}
            rightSlot={rightSlot}
            deltaMode={deltaMode}
            isVisiblyFlush={isVisiblyFlush}
            isTabBarSticky={isTabBarSticky}
            probeRef={probeRef}
            intendedCardsWidth={intendedCardsWidth}
          />
        )}

        <NodeLayoutBody
          store={store}
          objectDefinition={objectDefinition}
          tabGroups={tabGroups}
          hiddenGroupIds={predicateHiddenGroupIds}
          activeGroupId={activeGroupId}
          setActiveGroupId={setActiveGroupId}
          onGroupChange={onGroupChange}
          layoutMode={layoutMode}
          autofocus={autofocus}
          isLoading={isLoading}
          isSingleClick={isSingleClick}
          size={size}
          isMultiline={isMultiline}
          isInPopper={isInPopper}
          contentAutoSize={contentAutoSize}
          deltaMode={deltaMode}
          deltaAppearance={deltaAppearance}
          viewMode={viewMode}
          fieldVariant={fieldVariant}
          collapseThreshold={collapseThreshold}
          isVisiblyFlush={isVisiblyFlush}
          changeVariant={changeVariant}
          allSectionsExpanded={allSectionsExpanded}
          onAllSectionsExpandedChange={onAllSectionsExpandedChange}
          onKeyDown={onKeyDown}
          contextId={contextId}
          contextRef={ref}
          contextNode={node}
          contentContainerRef={containerRef}
          getCellPropsRef={getCellPropsRef}
        />
      </Scope>
    </NodeLayoutStoreProvider>
  );
});

type NodeLayoutBodyProps = {
  store: UseBoundStore<StoreApi<NodeStore>>;
  objectDefinition: (typeof Objects)[keyof typeof Objects] | undefined;
  tabGroups: TabGroupDef[];
  /** Groups whose own ``isVisible`` predicate currently excludes them. */
  hiddenGroupIds: string[];
  activeGroupId: string;
  setActiveGroupId: React.Dispatch<React.SetStateAction<string>>;
  onGroupChange?: (groupId: string) => void;
  layoutMode: "tabs" | "cards";
  autofocus: boolean;
  isLoading: boolean;
  isSingleClick: boolean;
  size: "sm" | "md";
  isMultiline: boolean;
  isInPopper: boolean;
  contentAutoSize: boolean;
  deltaMode: boolean;
  deltaAppearance?: DeltaAppearance;
  viewMode: "open" | "grid";
  fieldVariant: "node" | "cell";
  collapseThreshold?: number;
  isVisiblyFlush: boolean;
  changeVariant?: "create" | "update";
  allSectionsExpanded?: boolean;
  onAllSectionsExpandedChange?: (expanded: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  contextId: string;
  contextRef: React.RefObject<HTMLDivElement>;
  contextNode: ContextNode | null;
  contentContainerRef: React.RefObject<HTMLDivElement>;
  getCellPropsRef: React.MutableRefObject<
    ReturnType<typeof useContextCellNavigation>["getCellProps"] | undefined
  >;
};

function NodeLayoutBody({
  store,
  objectDefinition,
  tabGroups,
  hiddenGroupIds,
  activeGroupId,
  setActiveGroupId,
  onGroupChange,
  layoutMode,
  autofocus,
  isLoading,
  isSingleClick,
  size,
  isMultiline,
  isInPopper,
  contentAutoSize,
  deltaMode,
  deltaAppearance,
  viewMode,
  fieldVariant,
  collapseThreshold,
  isVisiblyFlush,
  changeVariant,
  allSectionsExpanded,
  onAllSectionsExpandedChange,
  onKeyDown,
  contextId,
  contextRef,
  contextNode,
  contentContainerRef,
  getCellPropsRef,
}: NodeLayoutBodyProps) {
  const typename = store((state) => state.typename);
  const allFieldDefinitions = store((state) => state.fieldDefinitions);

  const titleFieldKey = objectDefinition?.titleFieldKey as string | undefined;
  const fieldDefinitions = useMemo(
    () =>
      titleFieldKey && !deltaMode
        ? allFieldDefinitions.filter((fd) => fd.objectKey !== titleFieldKey)
        : allFieldDefinitions,
    [allFieldDefinitions, titleFieldKey, deltaMode],
  );

  const pendingChange = store(
    useShallow((state) => state.fieldValues?.pendingChange),
  ) as { deltas?: FieldDelta[] } | null | undefined;

  const deltaFieldNames = useMemo(() => {
    if (!deltaMode || !pendingChange?.deltas) return new Set<string>();
    return new Set(pendingChange.deltas.map((d) => d.fieldName));
  }, [deltaMode, pendingChange?.deltas]);

  // A new record's change is what it was given. Its empty fields were not
  // part of it, and listing them showed the whole record instead. Read as the
  // keys it was given, not the record: read whole, every keystroke drew every
  // row again.
  const givenKeys = store(
    useShallow((state) =>
      changeVariant !== "create"
        ? []
        : Object.entries((state.fieldValues ?? {}) as Record<string, unknown>)
            .filter(([, value]) => {
              if (value == null || value === "") return false;
              if (Array.isArray(value)) return value.length > 0;
              if (typeof value === "object") {
                return Object.keys(value).length > 0;
              }
              return true;
            })
            .map(([key]) => key),
    ),
  );

  const rowHasDelta = useCallback(
    (fd: (typeof allFieldDefinitions)[number]) => {
      if (changeVariant === "create") {
        return givenKeys.includes(fd.objectKey as string);
      }
      return (
        deltaFieldNames.has(fd.objectKey) ||
        (fd.inputKey != null && deltaFieldNames.has(fd.inputKey))
      );
    },
    [deltaFieldNames, changeVariant, givenKeys],
  );

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  const prevAllExpanded = useRef(allSectionsExpanded);
  useEffect(() => {
    if (prevAllExpanded.current && !allSectionsExpanded) {
      setExpandedSections(new Set());
    }
    prevAllExpanded.current = allSectionsExpanded;
  }, [allSectionsExpanded]);

  const toggleSection = useCallback(
    (sectionKey: string) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(sectionKey)) {
          next.delete(sectionKey);
        } else {
          next.add(sectionKey);
        }
        return next;
      });
      onAllSectionsExpandedChange?.(true);
    },
    [onAllSectionsExpandedChange],
  );

  const groups = useMemo((): NodeGroupType[] => {
    const groupList = [...tabGroups];
    const groupMap: Record<string, NodeGroupType> = {};
    groupList.forEach((group) => {
      groupMap[group.id] = { ...group, fieldDefinitions: [] };
    });

    fieldDefinitions.forEach((fieldDefinition) => {
      const groupId = fieldDefinition.groupId || "overview";
      const tabGroup = groupList.find((group) => group.id === groupId);
      if (tabGroup?.component) {
        return;
      }
      // A group hidden by its own predicate takes its fields with it. Without
      // this they fall through to the synthesis below and reappear as a bare
      // group of their own, detached from the stack the group belonged to.
      if (hiddenGroupIds.includes(groupId)) {
        return;
      }
      if (!groupMap[groupId]) {
        groupMap[groupId] = {
          id: groupId,
          label: groupId,
          fieldDefinitions: [],
        };
      }
      groupMap[groupId].fieldDefinitions!.push(fieldDefinition);
    });

    // What the change moved that no row shows - a field the record's object
    // file does not describe (a foreign key it never shows, a column with no
    // field), or one kept off the overview - is a changed row like any other,
    // at the end of the overview, drawn as the kind of field the model says
    // it is. Dropped, a change made only of these opened onto nothing.
    const overview = groupMap.overview;
    if (overview && deltaMode && changeVariant !== "create") {
      for (const delta of pendingChange?.deltas ?? []) {
        const isShown = overview.fieldDefinitions!.some(
          (fd) =>
            fd.objectKey === delta.fieldName || fd.inputKey === delta.fieldName,
        );
        if (isShown) continue;
        const described = objectDefinition?.fieldDefinitions?.find(
          (fd) =>
            fd.objectKey === delta.fieldName || fd.inputKey === delta.fieldName,
        );
        const sample = delta.currentValue ?? delta.originalValue;
        const kind =
          DELTA_FIELD_KINDS[delta.fieldType ?? ""] ??
          (typeof sample === "boolean"
            ? DELTA_FIELD_KINDS.BooleanField
            : typeof sample === "number"
              ? DELTA_FIELD_KINDS.FloatField
              : DELTA_FIELD_KINDS.CharField);
        const referenced = Array.isArray(sample) ? sample[0] : sample;
        const referencedTypename =
          referenced && typeof referenced === "object"
            ? (referenced as { __typename?: unknown }).__typename
            : undefined;
        const words = delta.fieldName.replace(/([A-Z])/g, " $1").toLowerCase();
        const label =
          described?.label ?? words.charAt(0).toUpperCase() + words.slice(1);
        const icon =
          (typeof referencedTypename === "string" &&
            iconsByTypename[
              referencedTypename as keyof typeof iconsByTypename
            ]) ||
          kind.ReactIcon;
        // A list of records - the lines a statement brought in - is said by
        // how many there are: drawn as references, each one's insides read
        // "[object Object]". The same number either side is no change to show.
        if (
          Array.isArray(delta.currentValue) ||
          Array.isArray(delta.originalValue)
        ) {
          const noun = label.toLowerCase();
          const counted = (value: unknown) => {
            const count = Array.isArray(value) ? value.length : 0;
            return `${count} ${count === 1 ? noun.replace(/s$/, "") : noun}`;
          };
          if (counted(delta.originalValue) === counted(delta.currentValue)) {
            continue;
          }
          overview.fieldDefinitions!.push({
            objectKey: delta.fieldName,
            label,
            type: "text",
            ReactIcon: icon,
            getValue: (data: Record<string, unknown>) =>
              counted(
                data[delta.fieldName] === undefined
                  ? delta.currentValue
                  : data[delta.fieldName],
              ),
            asString: (value: unknown) => String(value ?? ""),
          } as unknown as ObjectFieldDefinitionType<IObject>);
          continue;
        }
        overview.fieldDefinitions!.push({
          objectKey: delta.fieldName,
          label,
          type: kind.type,
          ReactIcon: icon,
          // The record's data may not carry the field; the change does. (The
          // before side is the record with the change undone, so it does.)
          getValue: (data: Record<string, unknown>) =>
            data[delta.fieldName] === undefined
              ? delta.currentValue
              : data[delta.fieldName],
          getValueOptions: () => ({ style: "related", isNullable: true }),
          asString: (value: unknown) => String(value ?? ""),
        } as unknown as ObjectFieldDefinitionType<IObject>);
      }
    }

    const resultGroups = Object.values(groupMap);
    return [
      ...groupList.map((g) => groupMap[g.id]),
      ...resultGroups.filter((g) => !groupList.find((gg) => gg.id === g.id)),
    ];
  }, [
    tabGroups,
    fieldDefinitions,
    hiddenGroupIds,
    deltaMode,
    changeVariant,
    pendingChange?.deltas,
    objectDefinition?.fieldDefinitions,
  ]);

  const { entity } = useNodeEntity(store);
  const { mutate: updateNode } = useBackendMutation({
    mutation: Mutations.UPDATE_NODE,
  });
  useLayoutEffect(() => {
    const getCommands = objectDefinition?.getCommands as
      | ((ctx: {
          data: unknown;
          entity: typeof entity;
          updateNode: typeof updateNode;
        }) => CommandDefinitionType[])
      | undefined;
    if (!getCommands || isLoading) return;

    // Built again as the record changes - followed here, not read in render,
    // where it drew every row again on every keystroke.
    let ids: string[] = [];
    const registerCommands = (fieldValues: NodeStore["fieldValues"]) => {
      ids.forEach(State.getState().unregisterCommandDefinition);
      ids = [];
      // An id alone no longer means the record is here: a detail page seeds
      // the store with an id-only stub so its group queries can start
      // immediately, and commands built from that stub read fields the record
      // has not brought back yet.
      if (!fieldValues?.id) return;
      for (const cmd of getCommands({
        data: fieldValues,
        entity: entity ?? null,
        updateNode,
      })) {
        State.getState().registerCommandDefinition(cmd);
        ids.push(cmd.id);
      }
    };
    registerCommands(store.getState().fieldValues);
    const unsubscribe = store.subscribe((state, prev) => {
      if (!shallow(state.fieldValues, prev.fieldValues)) {
        registerCommands(state.fieldValues);
      }
    });

    return () => {
      unsubscribe();
      ids.forEach(State.getState().unregisterCommandDefinition);
    };
  }, [objectDefinition?.getCommands, store, entity, updateNode, isLoading]);

  const activeGroup = groups.find((group) => group.id === activeGroupId);
  const activeGroupFieldDefinitions = activeGroup?.fieldDefinitions || [];
  const activeGroupHasComponent = Boolean(activeGroup?.component);
  const cardsGridHasEmbeddedContent =
    layoutMode === "cards" && groups.some((group) => group.component);
  const usesEmbeddedScrollLayout =
    contentAutoSize ||
    (layoutMode === "cards"
      ? cardsGridHasEmbeddedContent
      : activeGroupHasComponent);

  const rootRef = useRef<HTMLDivElement>(null);
  const [initialize] = useOverlayScrollbars({});

  const { getCellProps } = useContextCellNavigation(
    contextId,
    contextRef,
    contextNode,
    {
      rowCount: activeGroupFieldDefinitions.length,
      colCount: 1,
      wrap: false,
    },
  );

  getCellPropsRef.current = getCellProps;

  const hasAutofocusedRef = useRef(false);

  useEffect(() => {
    if (!autofocus) {
      hasAutofocusedRef.current = false;
    }
  }, [autofocus, store]);

  useEffect(() => {
    if (autofocus && !isLoading && activeGroupFieldDefinitions.length > 0) {
      if (hasAutofocusedRef.current) return;
      hasAutofocusedRef.current = true;
      requestAnimationFrame(() => {
        const container = contentContainerRef.current;
        if (!container) return;
        const firstFieldEl = container.querySelector(
          `[data-row-index="0"][data-col-index="0"]`,
        ) as HTMLElement;
        if (firstFieldEl) firstFieldEl.focus();
      });
    }
  }, [
    autofocus,
    isLoading,
    activeGroupFieldDefinitions.length,
    store,
    contentContainerRef,
  ]);

  useEffect(() => {
    if (rootRef.current && !usesEmbeddedScrollLayout) {
      initialize(rootRef.current);
    }
  }, [initialize, usesEmbeddedScrollLayout]);

  const labelWidth = DEFAULT_LABEL_WIDTH[size];

  const sharedGroupFieldsProps = {
    store,
    isSingleClick,
    isLoading,
    size,
    getCellProps,
    isMultiline,
    deltaMode,
    deltaAppearance,
    changeVariant,
    viewMode,
    fieldVariant,
    isVisiblyFlush,
    collapseThreshold,
    labelWidth,
    deltaFieldNames,
    rowHasDelta,
    expandedSections,
    toggleSection,
    allSectionsExpanded,
  };

  // A group's title row: its label, where nothing else names it, and its
  // toolbar.
  const renderGroupTitleRow = (group: NodeGroupType, showLabel: boolean) => {
    const GroupToolbar = group.toolbar;
    return (
      <div
        className={cn(
          "shrink-0 flex items-center gap-2 h-12 min-h-12 max-h-12",
          group.flush ? "pl-3 pr-3" : size === "md" ? "pl-4 pr-4" : "pl-2 pr-2",
          "min-w-0 [border-bottom-width:1px] [border-bottom-style:solid] border-border",
        )}
      >
        {showLabel && (
          <Typography level="title-md" className="truncate min-w-0 shrink-0">
            {group.label}
          </Typography>
        )}
        {GroupToolbar && (
          <div
            data-testid={`node-group-toolbar-${group.id}`}
            // At the far end beside a label; where a tab bar names the
            // group and the row is the toolbar's alone, it starts the row.
            className={cn(
              showLabel ? "ml-auto" : "ml-0",
              "min-w-0 flex items-center",
            )}
          >
            <GroupToolbar size={size} isInPopper={isInPopper} />
          </div>
        )}
      </div>
    );
  };

  const renderCardGroupPanel = (
    group: NodeGroupType,
    options?: { embedded?: boolean; stackIndex?: number; hideLabel?: boolean },
  ) => {
    const embedded = options?.embedded ?? false;
    const stackIndex = options?.stackIndex ?? 0;
    const autoSize = group.card?.stackAutoSize ?? false;
    const panelMinWidth = extractCardMinWidth(group.card);
    const panelMinSize = panelMinWidth > 0 ? Math.max(200, panelMinWidth) : 15;
    const GroupComponent = group.component;
    // A tabbed stack names its groups in the tab bar, so the per-card title
    // would just repeat the selected tab.
    const showLabel =
      !options?.hideLabel && (group.card?.isLabelVisible ?? true);

    const panelBody = (
      <>
        {(showLabel || group.toolbar) && renderGroupTitleRow(group, showLabel)}
        {GroupComponent ? (
          <div
            className={cn(
              autoSize ? "shrink-0" : "flex-1 min-h-0",
              "min-w-0 flex flex-col",
              group.flush
                ? "overflow-hidden"
                : size === "md"
                  ? "overflow-auto p-4"
                  : "overflow-auto p-2",
            )}
          >
            <GroupComponent size={size} />
          </div>
        ) : (
          <div
            className={cn(
              autoSize ? "shrink-0" : "flex-1 min-h-0",
              "min-w-0 flex flex-col",
              group.flush
                ? "overflow-hidden"
                : size === "md"
                  ? "overflow-auto p-4"
                  : "overflow-auto p-2",
            )}
          >
            <NodeGroupFields group={group} {...sharedGroupFieldsProps} />
          </div>
        )}
      </>
    );

    if (embedded) {
      return (
        <div
          key={group.id}
          className={cn(
            "flex flex-col min-w-0",
            stackIndex > 0 &&
              "[border-top-width:1px] [border-top-style:solid] border-border",
            autoSize ? "flex-[0_0_auto]" : "flex-1 min-h-0 overflow-hidden",
          )}
        >
          {panelBody}
        </div>
      );
    }

    return (
      <Panel
        key={group.id}
        id={group.id}
        minSize={panelMinSize}
        style={{
          minWidth: 0,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {panelBody}
      </Panel>
    );
  };

  const renderGroups = () => {
    if (layoutMode === "cards") {
      const cardItems = partitionCardLayoutGroups(groups);
      const cardsDefaultLayout = buildCardsDefaultLayoutFromItems(cardItems);

      return (
        <PanelGroup
          orientation="horizontal"
          defaultLayout={cardsDefaultLayout}
          // As tall as the column holds it, and not ``100%`` of it, as
          // ``AppLayoutPanelGroup`` is.
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
            height: "auto",
          }}
        >
          {cardItems.flatMap((item, index) => {
            const elements: React.ReactNode[] = [];
            if (index > 0) {
              elements.push(
                <AppLayoutPanelResizeHandle
                  key={`separator-${
                    item.type === "stack" ? item.stackId : item.group.id
                  }`}
                />,
              );
            }

            if (item.type === "group") {
              const group = groups.find((g) => g.id === item.group.id);
              if (!group) return elements;
              elements.push(renderCardGroupPanel(group));
              return elements;
            }

            const stackPanelId = `stack:${item.stackId}`;
            const stackLayout = buildStackDefaultLayout(item.groups);
            const stackMinWidth = cardLayoutItemMinWidth(item);
            const stackMinSize =
              stackMinWidth > 0 ? Math.max(200, stackMinWidth) : 15;
            const useFlexStack = item.groups.some(
              (stackGroup) =>
                groups.find((group) => group.id === stackGroup.id)?.card
                  ?.stackAutoSize,
            );
            // A tabbed stack reuses the layout-wide active group so the
            // selection survives switching between split and tabs, falling
            // back to the stack's first group when the active one lives in
            // another column (e.g. Preview).
            const activeStackGroup =
              item.mode === "tabs"
                ? (groups.find(
                    (group) =>
                      group.id === activeGroupId &&
                      item.groups.some(
                        (stackGroup) => stackGroup.id === group.id,
                      ),
                  ) ?? groups.find((group) => group.id === item.groups[0]?.id))
                : undefined;

            elements.push(
              <Panel
                key={stackPanelId}
                id={stackPanelId}
                minSize={stackMinSize}
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {item.mode === "tabs" ? (
                  <>
                    <NodeTabLayout
                      groups={item.groups.map((stackGroup) => ({
                        id: stackGroup.id,
                        label:
                          groups.find((group) => group.id === stackGroup.id)
                            ?.label ?? stackGroup.id,
                      }))}
                      activeGroupId={activeStackGroup?.id ?? ""}
                      setActiveGroupId={setActiveGroupId}
                      onGroupChange={onGroupChange}
                      size={size}
                    />
                    {activeStackGroup &&
                      renderCardGroupPanel(activeStackGroup, {
                        embedded: true,
                        hideLabel: true,
                      })}
                  </>
                ) : useFlexStack ? (
                  <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {item.groups.map((stackGroup, stackIndex) => {
                      const group = groups.find((g) => g.id === stackGroup.id);
                      if (!group) return null;
                      return renderCardGroupPanel(group, {
                        embedded: true,
                        stackIndex,
                      });
                    })}
                  </div>
                ) : (
                  <PanelGroup
                    orientation="vertical"
                    defaultLayout={stackLayout}
                    style={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      height: "auto",
                    }}
                  >
                    {item.groups.flatMap((stackGroup, stackIndex) => {
                      const stackElements: React.ReactNode[] = [];
                      if (stackIndex > 0) {
                        stackElements.push(
                          <AppLayoutPanelResizeHandle
                            key={`stack-separator-${stackGroup.id}`}
                            orientation="vertical"
                          />,
                        );
                      }
                      const group = groups.find((g) => g.id === stackGroup.id);
                      if (group) {
                        stackElements.push(renderCardGroupPanel(group));
                      }
                      return stackElements;
                    })}
                  </PanelGroup>
                )}
              </Panel>,
            );
            return elements;
          })}
        </PanelGroup>
      );
    }

    return (
      <div
        className={cn(
          "flex flex-col gap-px w-full max-w-full",
          activeGroupHasComponent && "flex-1 min-h-0 h-full",
        )}
      >
        {groups.map((group) => {
          const isActive = group.id === activeGroupId;
          if (group.component) {
            const GroupComponent = group.component;
            return isActive ? (
              <div
                key={group.id}
                className="flex flex-col flex-1 min-h-0 w-full max-w-full overflow-hidden"
              >
                {/* The tab bar names the group; its toolbar still has a row. */}
                {group.toolbar && renderGroupTitleRow(group, false)}
                <GroupComponent size={size} />
              </div>
            ) : null;
          }
          return (
            <NodeGroupContainer
              key={group.id}
              isVisible={isActive}
              layoutMode={layoutMode}
              card={group.card}
              isLabelVisible={group.card?.isLabelVisible ?? true}
            >
              {isActive && (
                <NodeGroupFields
                  key={`${typename}-${group.id}`}
                  group={group}
                  {...sharedGroupFieldsProps}
                />
              )}
            </NodeGroupContainer>
          );
        })}
      </div>
    );
  };

  return (
    <div
      key={`${layoutMode}-${usesEmbeddedScrollLayout ? "embedded" : "scroll"}`}
      data-overlayscrollbars-initialize={
        usesEmbeddedScrollLayout ? undefined : ""
      }
      ref={rootRef}
      // A cell stacks its own layers - content, the navigation overlay, the
      // one being edited - in the thousands. Kept to this box, they order
      // themselves without rising over the title and tabs the record sticks
      // above its fields.
      className={cn(
        "flex flex-col w-full max-w-full isolate",
        contentAutoSize
          ? "flex-[0_0_auto] min-h-auto overflow-visible"
          : "flex-1 min-h-0 overflow-hidden",
        usesEmbeddedScrollLayout ? "overflow-y-hidden" : "overflow-y-scroll",
        "relative",
        layoutMode === "cards"
          ? "p-0"
          : isVisiblyFlush || activeGroup?.flush
            ? "p-0"
            : size === "md"
              ? "p-4"
              : "p-2",
      )}
      onKeyDown={onKeyDown}
    >
      {renderGroups()}
    </div>
  );
}
